import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Script from '~/integrations/Script';
import clamp from '~/utils/clamp';

// TODO revise typings of ZAFClient and related types

const zendeskSizeLimits = {
  // width is omitted, as a user can resize flexible frames
  height: { min: 256, max: 512 }, // ? would like to be able to adjust the maximum based on the parent viewport height, so that it can reasonably fill more space if available
};

type RequestOptions<CompleteResponse extends boolean = false> = Partial<{
  /** Maps a given data type to its MIME type, which gets sent in the "Accept" request header. */
  accepts: Record<string, unknown>;
  /** If `true`, overrides the default auto-retry behavior of the request in response to rate-limiting. */
  autoRetry: boolean;
  /** If `false`, forces requested pages not to be cached by the browser. */
  cache: boolean;
  /**
   * Specifies the content type when sending data to a server.
   * If set to `false`, then no content type header is set.
   * Default value: `'application/x-www-form-url-encoded; charset=UTF-8';`.
   * Example: `'application/json'`.
   */
  contentType: false | string;
  /**
   * If `true`, makes a request from the browser.
   * If `false`, makes a request from a Zendesk proxy server.
   * Defaults to `false`.
   * Requests to Zendesk APIs are always made from the browser, regardless of this option.
   */
  cors: boolean;
  /** If `true`, forces a cross-domain request on the same domain. */
  crossDomain: boolean;
  /**
   * Data sent to the server.
   * Converted to a query string if not already a string.
   * If you pass a JavaScript object or array, and you specify `'application/json'` as the `contentType`, use the [`JSON.stringify()` method](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/stringify) to ensure the data is a JSON string.
   * For example: `data: JSON.stringify({ keyExample: 'Value Example' })`.
   */
  data: Record<string, unknown> | string | unknown[];
  /** Type of data expected back from the server. */
  dataType: 'text' | 'json';
  /** Additional header key/value pairs to send along with the request. */
  headers: Record<string, unknown>;
  /**
   * Flag to get a full response back, including the headers and response status.
   * Default value is `false`.
   */
  httpCompleteResponse: CompleteResponse;
  /** Allows the request to be successful only if the response has changed since the last request. */
  ifModified: boolean;
  /** A JSON web token. See [Encoding and sending ZAF JWT's](https://developer.zendesk.com/documentation/apps/getting-started/making-api-requests-from-a-zendesk-app/#encoding-and-sending-zaf-jwts). */
  jwt: Record<string, unknown>;
  /** Mime type to override the XHR mime type. */
  mimeType: string;
  /** Flag to allow secure settings to be used. */
  secure: boolean;
  /** Timeout period in milliseconds for the request. */
  timeout: number;
  /** If `true`, uses the traditional style of parameter serialization. */
  traditional: boolean;
  /** The HTTP method to use. */
  type: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  /** URL of requested resource. */
  url: string;
  /** Native [XMLHttpRequest properties](https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest#Properties), such as `withCredentials: true` for cross-domain requests. */
  xhrFields: unknown; // TODO type if desired to be used.
}>;

type HttpCompleteResponse<T> = {
  responseJSON: T;
  responseText: string;
  status: number;
  statusText: 'success';
  headers: Record<string, unknown>;
};

type ZAFEvent = string; // TODO create union of events from: `CoreAppEvent`, `SupportAppEvent`, `ChatAppEvent`, and `SellAppEvent`.

/**
 * Supported paths change based on the current UI; some paths may only be available in certain Zendesk products, locations or contexts.
 */
// ! ADD WITH CAUTION
type ZendeskGetData = {
  'currentUser.email': string;
  'ticket.requester.email': string;
  // fallback for unknown paths
  [key: string]: unknown;
};

/**
 * Zendesk Apps Framework (ZAF) v2.0
 * Expects the framework script to be already loaded: https://static.zdassets.com/zendesk_app_framework_sdk/2.0/zaf_sdk.min.js
 * Provides intellisense and typing.
 * @see https://developer.zendesk.com/documentation/apps/app-developer-guide/using-the-apps-framework/
 *
 * Further JSDoc comments either taken or inspired by the Zendesk API reference.
 * @see https://developer.zendesk.com/api-reference/apps/introduction/
 */
type ZAFClient = {
  /**
   * Requests context for the app, such as the host and location.
   * Depending on the location, the context may provide additional identifiers that you can use with the REST API to request additional data.
   */
  context(): Promise<{
    instanceGuid: string;
    product: string;
    account: {
      subdomain: string;
    };
    location: string;
    ticketId: number;
  }>;
  /**
   * Gets data from the UI asynchronously. For a complete list of supported paths, see:
   *
   * - [Core Apps API](https://developer.zendesk.com/api-reference/apps/apps-core-api/core_api/)
   * - [Support Apps API](https://developer.zendesk.com/api-reference/apps/apps-support-api/introduction/)
   * - [Chat Apps API](https://developer.zendesk.com/api-reference/apps/apps-chat-api/introduction/)
   * - [Sell Apps API](https://developer.zendesk.com/api-reference/apps/apps-sell-api/introduction/)
   *
   * Some path segments can take arguments that you can specify using a colon syntax.
   * For example, to retrieve information about the options available to the `type` ticket field, you could call `client.get('ticketField:type.options')`.
   */
  get<T extends keyof ZendeskGetData>(
    path: T,
  ): Promise<{ [K in T]?: ZendeskGetData[K] } & { errors: unknown }>;
  get<T extends readonly (keyof ZendeskGetData)[]>(
    paths: T, // bulk get https://developer.zendesk.com/documentation/apps/app-developer-guide/using-the-apps-framework/
  ): Promise<{ [K in T[number]]: ZendeskGetData[K] } & { errors: unknown }>;
  /**
   * Returns whether or not an event has the specified handler attached to it.
   */
  has(name: ZAFEvent, handler: (event: unknown) => void): boolean;
  /**
   * Initialize a `ZAFClient` for another location.
   * Note that instances are transient in some locations (like ticket sidebar), so if the ticket tab is closed that instance is destroyed.
   */
  instance(instanceGuid: string): ZAFClient;
  /**
   * Executes an action identified by the `name` path parameter, or multiple actions as defined in the `paths` argument, within the product interface.
   * For a complete list of supported paths, see:
   *
   * - [Core Apps API](https://developer.zendesk.com/api-reference/apps/apps-core-api/core_api/)
   * - [Support Apps API](https://developer.zendesk.com/api-reference/apps/apps-support-api/introduction/)
   * - [Chat Apps API](https://developer.zendesk.com/api-reference/apps/apps-chat-api/introduction/)
   * - [Sell Apps API](https://developer.zendesk.com/api-reference/apps/apps-sell-api/introduction/)
   *
   * Some path segments can take arguments that you can specify using a colon syntax.
   * For example, to hide the priority ticket field, call `client.invoke('ticketFields:priority.hide')`.
   */
  invoke<T extends string>(
    name: T,
    ...args: unknown[]
  ): Promise<Record<T, unknown>>;
  invoke<T extends string>(
    paths: Record<T, unknown[]>,
  ): Promise<Record<T, unknown>>;
  /**
   * Requests metadata for the app, such as the app id, installation id, app plan information, and Stripe subscription id (if applicable).
   * Generic type provided for expected type of `settings` object.
   *
   * @see https://developer.zendesk.com/api-reference/apps/apps-core-api/client_api/#clientmetadata
   */
  metadata<T = unknown>(): Promise<{
    appId: number;
    name: string;
    /** Although the documentation shows an example as a number, it actually appears to return a `string`. */
    installationId: string;
    version: string;
    settings: T;
  }>;
  /**
   * Allows you to remove a handler for a framework event.
   */
  off(name: ZAFEvent, handler: () => void): void;
  /**
   * Allows you to add handlers to a framework event.
   * You can add as many handlers as you wish.
   * They will be executed in the order they were added.
   *
   * For available events, see:
   *
   * - [Core Apps API](https://developer.zendesk.com/api-reference/apps/apps-core-api/core_api/)
   * - [Support Apps API](https://developer.zendesk.com/api-reference/apps/apps-support-api/introduction/)
   * - [Chat Apps API](https://developer.zendesk.com/api-reference/apps/apps-chat-api/introduction/)
   * - [Sell Apps API](https://developer.zendesk.com/api-reference/apps/apps-sell-api/introduction/)
   *
   * See also [Working with framework events](https://developer.zendesk.com/documentation/apps/app-developer-guide/using-the-apps-framework/#working-with-framework-events).
   *
   * @param context the value of `this` within your handler
   */
  on<T>(name: ZAFEvent, handler: () => void, context?: T): unknown;
  /**
   * Makes an HTTP request.
   * See [Making API requests from a Zendesk app](https://developer.zendesk.com/documentation/apps/getting-started/making-api-requests-from-a-zendesk-app/).
   *
   * Generic provided for expected return type; however, doesn't guarantee type.
   *
   * @param options the url of the resource to request, or a `RequestOptions` object with their supported, case-sensitive, values.
   */
  request<T = unknown>(url: string): Promise<T>;
  request<T = unknown>(
    // disabled for clarity of overload types with differing parameter names
    // oxlint-disable-next-line @typescript-eslint/unified-signatures
    options: RequestOptions,
  ): Promise<T>;
  request<T = unknown>(
    options: RequestOptions<true>,
  ): Promise<HttpCompleteResponse<T>>;
  /**
   * Sets data in the UI asynchronously.
   * For a complete list of supported paths, see:
   *
   * - [Core Apps API](https://developer.zendesk.com/api-reference/apps/apps-core-api/core_api/)
   * - [Support Apps API](https://developer.zendesk.com/api-reference/apps/apps-support-api/introduction/)
   * - [Chat Apps API](https://developer.zendesk.com/api-reference/apps/apps-chat-api/introduction/)
   * - [Sell Apps API](https://developer.zendesk.com/api-reference/apps/apps-sell-api/introduction/)
   *
   * Some path segments can take arguments you can specify using a colon syntax.
   * For example, to set the due date of a task, you could call `client.set('ticket.customField:due_date', new Date())`.
   */
  set<T extends string>(path: T): Promise<Record<T, unknown>>;
  set<T extends readonly string[]>(
    data: Record<T[number], unknown>,
  ): Promise<Record<T[number], unknown>>;
  /** Triggers the specified event on the client. */
  trigger(name: ZAFEvent, data?: unknown): void;
};

declare global {
  // oxlint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    ZAFClient: {
      init(): ZAFClient | false; // client load in an invalid context appears to return false
    };
    // prefer using this global for access alike a singleton
    Zendesk?: {
      client: ZAFClient;
      resize(): void;
    };
  }
}

function ZendeskResize() {
  useEffect(() => {
    window.Zendesk?.resize(); // initial resize on mount
    const observer = new MutationObserver(() => window.Zendesk?.resize());

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, []);

  return null;
}

/**
 * Zendesk integration component.
 * Adds the Zendesk App Framework SDK script.
 * @see https://developer.zendesk.com/documentation/apps/app-developer-guide/using-the-apps-framework/
 */
// the merchant UI is loaded within an iframe in the Zendesk sidebar, rendering from scratch for each ticket
export default function Zendesk() {
  const [, setSearchParams] = useSearchParams();
  return (
    <>
      <Script
        src="https://static.zdassets.com/zendesk_app_framework_sdk/2.0/zaf_sdk.min.js"
        onLoad={() => {
          const client = window.ZAFClient.init();
          if (!client) return;
          window.Zendesk = {
            client,
            // inspired by resize in the Kustomer integration
            /**
             * Resizing the app `iframe` to fit its content, within some reasonable height constraints to avoid extreme sizes; otherwise, modals or vertically centered content may be quickly pushed out of view.
             *
             * `flexible` will omit `width` and should fallback to responsive sizing based on the manifest option.
             * @see https://developer.zendesk.com/documentation/apps/app-developer-guide/manifest/#location:~:text=flexible
             */
            async resize(flexible = true) {
              const height = clamp(
                Math.max(
                  document.body.scrollHeight,
                  document.body.offsetHeight,
                  document.documentElement.clientHeight,
                  document.documentElement.scrollHeight,
                  document.documentElement.offsetHeight,
                ),
                zendeskSizeLimits.height.min,
                zendeskSizeLimits.height.max,
              );

              const width = Math.max(
                document.body.scrollWidth,
                document.body.offsetWidth,
                document.documentElement.clientWidth,
                document.documentElement.scrollWidth,
                document.documentElement.offsetWidth,
              );

              await client.invoke('resize', {
                width: flexible ? undefined : `${width}px`,
                height: `${height}px`,
              });
            },
          };

          Promise.all([
            client.get('currentUser.email'),
            client.get('ticket.requester.email'),
            client.metadata(),
          ])
            .then(
              ([
                { 'currentUser.email': email },
                { 'ticket.requester.email': customerEmail },
                metadata,
              ]) => {
                setSearchParams(
                  (searchParams) => {
                    // should already contain `origin` and some other parameters
                    searchParams.set('ref', 'zendesk'); // ensure ref is set to zendesk
                    searchParams.set('installationId', metadata.installationId); // for tracking and support, as well as any future use in the app itself

                    searchParams.set('email', email ?? ''); // email can't be set in the manifest, so set here
                    searchParams.set('customerEmail', customerEmail ?? '');

                    return searchParams;
                  },
                  { replace: true },
                );
              },
            )
            .catch((error) => {
              console.error('Error during Zendesk auth initialization:', error);
            });
        }}
      />
      <ZendeskResize />
    </>
  );
}
